import React, { useState, useEffect, useRef } from "react";
import { Container } from "reactstrap";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Modal,
  IconButton,
  Box,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Moment from "moment";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";

const LetterOfNecessityForm = (props) => {
  const {
    FormId,
    FormPatientId,
    PatientName,
    onFormSubmit,
    handleClose,
    PatientDob,
  } = props;

  const [patientDOB, setPatientDOB] = useState(
    Moment(PatientDob).format("YYYY-MM-DD"),
  );
  const [physicianDate, setPhysicianDate] = useState(
    Moment().format("YYYY-MM-DD"),
  );

  const fields = [
    "id",
    "patientId",
    "practiceId",
    "patientName",
    "patientDOB",
    "diagnosis",
    "oralApplianceType",
    "medicalNecessityStatement",
    "physicianName",
    "physicianAddress",
    "physicianSignature",
    "physicianNPI",
    "physicianDate",
    "createdDate",
    "createdBy",
    "updatedDate",
    "updatedBy",
    "inactive",
  ];

  const defValues = {
    id: 0,
    patientId: 0,
    practiceId: 0,
    patientName: "",
    patientDOB: "",
    diagnosis: "",
    oralApplianceType: "",
    medicalNecessityStatement: "",
    physicianName: "",
    physicianAddress: "",
    physicianSignature: "",
    physicianNPI: "",
    physicianDate: "",
    createdDate: "",
    createdBy: "",
    updatedDate: "",
    updatedBy: "",
    inactive: false,
  };

  const token = localStorage.getItem("Token");
  const auth = `Bearer ${token}`;
  const headers = { Authorization: auth };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: defValues,
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (FormId) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/PatientForms/GetFormLetterOfNecessityId?id=${FormId}`,
          { headers },
        )
        .then((response) => {
          fields.forEach((field) => {
            if (response[field] !== undefined) {
              setValue(field, response[field]);
            }
          });
          if (response.physicianDate) {
            setPhysicianDate(
              Moment(response.physicianDate).format("YYYY-MM-DD"),
            );
          }
        })
        .catch((error) => {
          console.error("Error loading form data:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to load form data",
          });
        });
    }
  }, [FormId]);

  const onSubmit = (data) => {
    setSubmitting(true);

    const postData = {
      ...data,
      patientId: FormPatientId,
      patientDOB: patientDOB,
      patientName: PatientName,
    };

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/PatientForms/AddFormLetterOfNecessity`,
        postData,
        { headers },
      )
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Form Added!",
          html: `<p>Letter of Medical Necessity has been saved successfully.</p>`,
          timer: 2000, // 2 seconds
          showConfirmButton: false,
          timerProgressBar: true,
          willOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) swalPopup.style.zIndex = 2200;
          },
        });

        if (onFormSubmit) {
          onFormSubmit(response.id);
        }

        if (handleClose) {
          handleClose();
        }
        setSubmitting(false);
      })
      .catch((error) => {
        setSubmitting(false);
        Swal.fire({
          icon: "error",
          title: "Failed!",
          html: `<p>${
            error.response?.data?.message ||
            "Something went wrong. Please try again."
          }</p>`,
          confirmButtonColor: "#F44336",
          willOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) swalPopup.style.zIndex = 2200;
          },
        });
      });
  };
  const [progressNoteDialogOpen, setProgressNoteDialogOpen] = useState(false);
  const [progressNoteContent, setProgressNoteContent] = useState("");
  const formRef = useRef(null);
  const [editorHTML, setEditorHTML] = useState("");
  const replaceInputsWithText = (container) => {
    if (!container) return;

    // Replace radios with ☑/☐
    const radios = container.querySelectorAll('input[type="radio"]');
    radios.forEach((radio) => {
      const checked = radio.checked ? "☑" : "☐";
      const span = document.createElement("span");
      span.innerHTML = `${checked}`;
      span.style.marginRight = "12px";
      span.style.display = "inline-block";
      span.style.lineHeight = "1.5"; // gives extra height
      radio.parentNode.replaceChild(span, radio);
    });

    // Replace checkboxes with ☑/☐
    const checkboxes = container.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      const checked = checkbox.checked ? "☑" : "☐";
      const span = document.createElement("span");
      span.innerHTML = `${checked}`;
      span.style.marginRight = "12px";
      span.style.display = "inline-block";
      span.style.lineHeight = "1.5"; // gives extra height
      checkbox.parentNode.replaceChild(span, checkbox);
    });

    // Replace all text/date/number inputs with bold text
    const textInputs = container.querySelectorAll(
      'input:not([type="radio"]):not([type="checkbox"])',
    );
    textInputs.forEach((input) => {
      const value = input.value || "-";
      const span = document.createElement("span");
      span.innerText = value;
      span.style.fontWeight = "bold";
      span.style.display = "inline-block";
      span.style.padding = "6px 8px"; // adds height/padding
      span.style.minWidth = "120px"; // optional: ensures spacing
      span.style.backgroundColor = "#f0f0f0"; // optional subtle background
      span.style.borderRadius = "4px";
      input.parentNode.replaceChild(span, input);
    });
  };

  const convertToProgressNote = (formData) => {
    if (!formRef.current) return;

    const formClone = formRef.current.cloneNode(true);
    replaceInputsWithText(formClone);

    const html = `
    <div
      style="
        font-family: Calibri, Segoe UI, sans-serif;
        color: #000;
        width: 100%;
        max-width: 100%;
        background: #ffffff;
        border: 1.5px solid #cfd8dc;
        border-radius: 10px;
        box-shadow: 0 4px 14px rgba(0,0,0,0.08);
        box-sizing: border-box;
        overflow-x: auto;
        padding: 10px;
      "
    >
      <div
        style="
          width: 100%;
          box-sizing: border-box;
          padding: 0;
          margin: 0;
        "
      >
        ${formClone.innerHTML}
      </div>
    </div>
  `;

    setEditorHTML(html);
    setProgressNoteDialogOpen(true);
  };

  const onSubmitProgresNote = (data) => {
    const today = new Date().toISOString().split("T")[0];
    const postData = {
      patientId: FormPatientId,
      patientNoteId: 0,
      noteText: editorHTML,
      signedDate: "",
      visitDate: today,
    };

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/PatientNote/AddPatientNote`,
        postData,
        { headers },
      )
      .then((response) => {
        setProgressNoteDialogOpen(false);
        handleClose();
        Swal.fire({
          icon: "success",
          timer: 1000,
          showConfirmButton: false,
          title: "Note saved successfully.",
          willOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "1800";
          },
        });
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: `Error ${error.message}`,
          timer: 1000,
          willOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "1800";
          },
        });
      });
  };
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Modal
        open={progressNoteDialogOpen}
        onClose={() => setProgressNoteDialogOpen(false)}
        aria-labelledby="progress-note-modal"
        sx={{
          zIndex: 1820,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Paper
          elevation={24}
          sx={{
            width: "98%",
            maxWidth: 1020,
            maxHeight: "95vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #2c5aa0 0%, #1976d2 100%)",
              color: "white",
              p: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#fff",
                }}
              >
                Progress Note
              </Typography>
            </Box>
            <IconButton
              onClick={() => setProgressNoteDialogOpen(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Toolbar */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              p: 1,
              backgroundColor: "#f9f9f9",
              borderBottom: "1px solid #ddd",
            }}
          >
            {[
              { label: "B", command: "bold", style: { fontWeight: "bold" } },
              { label: "I", command: "italic", style: { fontStyle: "italic" } },
              {
                label: "U",
                command: "underline",
                style: { textDecoration: "underline" },
              },
              {
                label: "S",
                command: "strikeThrough",
                style: { textDecoration: "line-through" },
              },
              { label: "Sub", command: "subscript" },
              { label: "Sup", command: "superscript" },
              { label: "• List", command: "insertUnorderedList" },
              { label: "1. List", command: "insertOrderedList" },
              { label: "Left", command: "justifyLeft" },
              { label: "Center", command: "justifyCenter" },
              { label: "Right", command: "justifyRight" },
              { label: "Justify", command: "justifyFull" },
              { label: "H1", command: "formatBlock", value: "h1" },
              { label: "H2", command: "formatBlock", value: "h2" },
              { label: "Quote", command: "formatBlock", value: "blockquote" },
              { label: "Code", command: "formatBlock", value: "pre" },
              { label: "Link", command: "createLink", prompt: true },
              { label: "Text Color", command: "foreColor", color: "#000000" },
              { label: "BG Color", command: "hiliteColor", color: "#FFFF00" },
            ].map((btn) => (
              <Button
                key={btn.label}
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent losing focus
                  if (btn.prompt) {
                    const url = prompt("Enter URL");
                    if (url) document.execCommand(btn.command, false, url);
                  } else if (btn.color) {
                    document.execCommand(btn.command, false, btn.color);
                  } else if (btn.value) {
                    document.execCommand(btn.command, false, btn.value);
                  } else {
                    document.execCommand(btn.command, false, null);
                  }
                }}
                sx={{
                  minWidth: 35,
                  fontWeight: "bold",
                  textTransform: "none",
                  ...btn.style,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              >
                {btn.label}
              </Button>
            ))}
          </Box>

          {/* Editable content */}
          <Box
            sx={{
              overflowY: "auto",
              backgroundColor: "#fdfdfd",
            }}
          >
            <div
              contentEditable
              suppressContentEditableWarning
              style={{
                minHeight: "75vh",
                border: "1px solid #ccc",
                borderRadius: 4,
                padding: "16px",
                fontFamily: "'Calibri', 'Segoe UI', sans-serif",
                fontSize: 14,
                backgroundColor: "#fff",
                overflowY: "auto",
              }}
              dangerouslySetInnerHTML={{ __html: editorHTML }}
            />
          </Box>

          <Grid
            container
            spacing={1}
            justifyContent="flex-end"
            sx={{
              bgcolor: "#f5f5f5",
              p: 1.5,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
            }}
          >
            <Grid item>
              <Button
                type="submit"
                variant="contained"
                onClick={onSubmitProgresNote}
                sx={{
                  minWidth: 200,
                  height: 40,
                  borderColor: "#4CAF50",
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#4CAF50",
                    color: "white",
                    borderColor: "#4CAF50",
                  },
                }}
              >
                Save Progress Note
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setProgressNoteDialogOpen(false)}
                disabled={submitting}
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Modal>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header Section */}
        <Grid container ref={formRef}>
          <Grid container spacing={3} sx={{ m: 2 }}>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                align="center"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                LETTER OF MEDICAL NECESSITY
              </Typography>
              <Typography
                variant="h6"
                align="center"
                fontWeight="medium"
                sx={{ mb: 3 }}
              >
                Referring MD's Written Order for Oral Appliance for OSA
              </Typography>
            </Grid>
          </Grid>

          {/* Patient Information Section */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Patient Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="patientName"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Patient Name"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={PatientName}
                    InputProps={{ readOnly: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="patientDOB"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Date of Birth"
                    type="date"
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={patientDOB || ""}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setPatientDOB(e.target.value);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Diagnosis Section */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#e8f4fd", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Diagnosis
              </Typography>
              <Controller
                name="diagnosis"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Diagnosis"
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Oral Appliance Type Section */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Oral Appliance Type
              </Typography>
              <Controller
                name="oralApplianceType"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Oral Appliance Type"
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={4}
                    {...field}
                  />
                )}
              />
              <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
                A custom-fabricated Oral Appliance for OSA is defined as one
                that is individually made for a specific patient (no other
                patient would be able to use this item) starting with basic
                materials. It involves substantial work to produce, usually by a
                specialized lab. It may involve the incorporation of some
                prefabricated components. It involves more than trimming,
                bending, or making other modifications to a substantially
                prefabricated item.
              </Typography>
            </Grid>
          </Grid>

          {/* Medical Necessity Statement */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#e8f4fd", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Medical Necessity Statement
              </Typography>
              <Controller
                name="medicalNecessityStatement"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Medical Necessity Statement"
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Physician Information Section */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Physician Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="physicianName"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Ordering Physician Name (Printed)"
                    size="small"
                    fullWidth
                    variant="outlined"
                    {...field}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="physicianNPI"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Physician's NPI Number"
                    size="small"
                    fullWidth
                    variant="outlined"
                    {...field}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="physicianAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Physician's Address"
                    size="small"
                    fullWidth
                    variant="outlined"
                    {...field}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="physicianSignature"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Physician's Signature"
                    size="small"
                    fullWidth
                    variant="outlined"
                    {...field}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="physicianDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Date"
                    type="date"
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    {...field}
                    value={physicianDate || ""}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setPhysicianDate(e.target.value);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        {/* Submit Button */}
        <Grid
          container
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
          sx={{ mt: 4 }}
        >
          {/* {FormId > 0 && ( */}
          <Grid item>
            <Button
              variant="contained"
              onClick={handleSubmit((data) => convertToProgressNote(data))}
              disabled={submitting}
              sx={{
                minWidth: 220,
                height: 42,
                backgroundColor: "#4CAF50",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#43A047",
                  boxShadow: "none",
                },
              }}
            >
              Convert to Progress Note
            </Button>
          </Grid>
          {/* // )} */}

          <Grid item>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                minWidth: 120,
                height: 42,
                backgroundColor: "#A574FD",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#8A5CE6",
                  boxShadow: "none",
                },
              }}
            >
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default LetterOfNecessityForm;
